import React, { useState } from 'react';
import get from 'lodash/get';
import difference from 'lodash/difference';
import pickBy from 'lodash/pickBy';
import isNil from 'lodash/isNil';
import each from 'lodash/each';
import pick from 'lodash/pick';
import Currency from '$app/components/vendor/Currency';
import { Tooltip } from 'antd';
import moment from 'moment';
import { poundsToPence, penceToPounds } from '$app/utils/util';
import WorkOperationsTable from '../WorkDetails/components/WorkDetailsTable/WorkOperationsTable';
import Markup from '../WorkDetails/components/WorkDetailsTable/Markup';
import Status from '../WorkDetails/components/WorkDetailsTable/Status';
import { renderLabel } from '../WorkDetails/components/WorkDetailsTable';
import { prepareTableData } from '../WorkDetails/selectors/quoteWorkOperationsSelector';
import { makeCampaignRows } from '../WorkDetails/selectors/campaignSelector';
import { makeCreditRows } from '../WorkDetails/selectors/creditSelector';
import {
  TRANSPORTATION_FEE_TYPE,
  ADJUSTMENT_TYPE,
  COLLECTION_AND_DELIVERY_TYPE,
  DISCOUNT_TYPE,
  COUPON_TYPE,
  CREDIT_TYPE,
  LINE_ITEMS_TABLE_DATA,
  LINE_ITEMS_PRICE_COLUMNS,
} from '../WorkDetails/constants';

import { HeaderDiv, LeftSide, TitleStyled, Container } from './styled';

function prepareDataSource(quote) {
  const { lineItems } = quote;

  const data = lineItems.map((li) => {
    li.key = li.operationId;

    const sub = get(li, 'subLineItems', []);

    if (sub.length > 0) {
      sub.map((sli) => {
        sli.key = sli.operationId;
        return sli;
      });
    }

    return li;
  });

  const approved = data.filter((li) => ['mot', 'service', 'other'].includes(li.type));
  const approvedItems =
    approved.length > 0
      ? prepareTableData(approved).map((li) => Object.assign(li, { urgency: get(li, 'urgency', 'other') }))
      : [];

  const campaigns = data.filter((li) => [COUPON_TYPE, DISCOUNT_TYPE].includes(li.type));
  const campaignItems = campaigns.length > 0 ? makeCampaignRows(campaigns) : [];

  const credits = data.filter((li) => [CREDIT_TYPE].includes(li.type));
  const creditItems = credits.length > 0 ? makeCreditRows(credits) : [];

  const cnds = data
    .filter((li) => li.type === COLLECTION_AND_DELIVERY_TYPE && li.accountCode !== '304')
    .map((cd) =>
      pickBy(
        cd,
        (val, key) =>
          !isNil(val) &&
          difference(LINE_ITEMS_TABLE_DATA, [
            'subLineItems',
            'labourTime',
            'labourCost',
            'partsQuantity',
            'partsPrice',
          ]).includes(key)
      )
    )
    .map((li) => {
      const { operationId, workSummary, title, source, status } = li;

      Object.assign(li, {
        key: operationId,
        workOperation: {
          value: source,
          text: workSummary || title,
        },
        className: ['collection-delivery-row', 'footer-row', status === 'deleted' && 'status-deleted'],
      });

      each(pick(li, LINE_ITEMS_PRICE_COLUMNS), (v, k) => (li[k] = penceToPounds(v)));
      return li;
    });
  const collectionDeliveryItems = cnds.length > 0 ? cnds : [];

  const transportFee = data.filter((li) => li.type === TRANSPORTATION_FEE_TYPE);
  const transportFeeItem = transportFee.length > 0 ? transportFee : [];

  const totals = prepareTableData([
    {
      garageCost: get(quote, 'garageTotalPriceWithoutVAT', 0),
      garageCostWithVat: get(quote, 'garageTotalPriceWithVAT', 0),
      fixedCost: get(quote, 'customerTotalPriceWithoutVAT', 0),
      fixedCostWithVAT: get(quote, 'customerTotalPriceWithVAT', 0),
      createdAt: get(quote, 'createdAt', ''),
      workSummary: 'Total',
    },
  ]).map((i) => ({ ...i, className: 'total-row', key: 'total-approved' }));

  return [
    ...approvedItems,
    ...campaignItems,
    ...creditItems,
    ...collectionDeliveryItems,
    ...transportFeeItem,
    ...totals,
  ].map((i) => ({ ...i, className: [...(i.className || []), `status-${i.status}`] }));
}

function workSummaryRender(val, wo) {
  const { sku, key, type, parentId } = wo;

  return !sku &&
    typeof key === 'number' && // exlcude Total
    ![COLLECTION_AND_DELIVERY_TYPE, ADJUSTMENT_TYPE, TRANSPORTATION_FEE_TYPE].includes(type) && // exclude Garage C&D and Adjustments
    !parentId // exclude children
    ? `Other - ${val}`
    : val;
}

function getColumns() {
  return [
    {
      title: '',
      key: 'description-group',
      children: [
        {
          title: 'Quote',
          key: 'workOperation',
          dataIndex: 'workSummary',
          width: 420,
          render: (val, wo) => {
            if (val !== 'Total') return <strike>{workSummaryRender(val, wo)}</strike>;
            return workSummaryRender(val, wo);
          },
        },
      ],
    },
    {
      title: 'Garage',
      key: 'garage-group',
      children: [
        {
          title: 'Qty',
          key: 'partsQuantity',
          align: 'right',
          width: 96,
          render: (val, { partsQuantity, labourTime, metadata: m = {}, type, quoteType }) => {
            if (labourTime && labourTime !== 0 && partsQuantity && partsQuantity !== 0) {
              return (
                <Fragment>
                  <strike>
                    {renderLabel('labour')} {labourTime}
                  </strike>
                  <br />
                  <br />
                  <strike>
                    {renderLabel('part')} {partsQuantity}
                  </strike>
                </Fragment>
              );
            }

            if (type === 'part') {
              return <strike>{partsQuantity}</strike>;
            }

            if (type === 'labour') {
              return <strike>{labourTime}</strike>;
            }

            return '';
          },
        },
        {
          title: 'Price',
          key: 'partsPrice',
          displayPrice: true,
          width: 96,
          align: 'right',
          render: (val, { partsPrice, labourCost, metadata: m = {}, type, quoteType }) => {
            if (labourCost && labourCost !== 0 && partsPrice && partsPrice !== 0) {
              return (
                <Fragment>
                  <strike>
                    {renderLabel('labour')} <Currency trailingZero>{poundsToPence(labourCost)}</Currency>
                  </strike>
                  <br />
                  <br />
                  <strike>
                    {renderLabel('part')} <Currency trailingZero>{poundsToPence(partsPrice)}</Currency>
                  </strike>
                </Fragment>
              );
            }

            if (type === 'part') {
              return (
                <strike>
                  <Currency trailingZero>{poundsToPence(partsPrice)}</Currency>
                </strike>
              );
            }

            if (type === 'labour') {
              return (
                <strike>
                  <Currency trailingZero>{poundsToPence(labourCost)}</Currency>
                </strike>
              );
            }

            return '';
          },
        },
        {
          title: 'Total (pre VAT)',
          key: 'garageCost',
          displayPrice: true,
          align: 'right',
          width: 132,
          render: (val, wo) => {
            if (wo.workSummary === 'Total') {
              return <Currency trailingZero>{poundsToPence(val)}</Currency>;
            }
            return (
              <strike>
                <Currency trailingZero>{poundsToPence(val)}</Currency>
              </strike>
            );
          },
        },
        {
          title: 'Total (inc VAT)',
          key: 'garageCostWithVat',
          displayPrice: true,
          width: 132,
          align: 'right',
          render: (val, wo) => {
            if (wo.workSummary === 'Total') {
              return <Currency trailingZero>{poundsToPence(val)}</Currency>;
            }

            return (
              <strike>
                <Currency trailingZero>{poundsToPence(val)}</Currency>
              </strike>
            );
          },
        },
      ],
    },
    {
      title: 'Customer',
      key: 'customer-group',
      align: 'center',
      children: [
        {
          title: 'Total Inc. VAT',
          key: 'fixedCostWithVAT',
          displayPrice: true,
          width: 142,
          align: 'center',
          render: (val, wo) => {
            if (`${val}`.includes('%')) return wo.workSummary === 'Total' ? { val } : <strike>{val}</strike>;
            return wo.workSummary === 'Total' ? (
              'default'
            ) : (
              <strike>
                <Currency trailingZero>{poundsToPence(val)}</Currency>
              </strike>
            );
          },
        },
        {
          title: 'Mark up',
          key: 'markup',
          align: 'center',
          width: 142,
          render: (val, { workSummary, parentId, type, garageCostWithVat, fixedCostWithVAT }) => {
            if (
              !parentId &&
              ![COLLECTION_AND_DELIVERY_TYPE, DISCOUNT_TYPE, TRANSPORTATION_FEE_TYPE].includes(type)
            ) {
              if (workSummary === 'Total') {
                return <Markup garageCostWithVat={garageCostWithVat} fixedCostWithVAT={fixedCostWithVAT} />;
              }
              return (
                <strike>
                  <Markup garageCostWithVat={garageCostWithVat} fixedCostWithVAT={fixedCostWithVAT} />
                </strike>
              );
            }
          },
        },
      ],
    },
    {
      title: '',
      key: 'action-group',
      children: [
        {
          title: 'Status',
          key: 'status',
          align: 'center',
          render: (val, item) => {
            const { parentId, createdAt, workSummary } = item;

            if (parentId) {
              // Created At on children only
              return (
                <Tooltip placement="left" title="Time &amp; date added">
                  <time
                    dateTime={val}
                    style={{ cursor: 'pointer', fontSize: 9, opacity: 0.4, fontWeight: 'bold' }}
                  >
                    {moment(createdAt).format('DD/MM HH:mm')}
                  </time>
                </Tooltip>
              );
            }

            return workSummary !== 'Total' && <Status>Cancelled</Status>;
          },
        },
      ],
    },
  ];
}

export default function CancelledWorkDetails({ cancelledQuote = {} }) {
  const [collapsedKeys, setCollapsedKeys] = useState([]);

  function getExpandedRowKeys(dataSource) {
    const keyWithChildren = dataSource.map(({ key, children }) => children && key);
    return difference(keyWithChildren, collapsedKeys);
  }

  function handleExpandRow(e, { key }) {
    if (collapsedKeys.includes(key)) {
      setCollapsedKeys(difference(collapsedKeys, [key]));
    } else {
      setCollapsedKeys([...collapsedKeys, key]);
    }
  }

  if (cancelledQuote.quoteId) {
    const dataSource = prepareDataSource(cancelledQuote);
    const columns = getColumns();
    const expandedRowKeys = getExpandedRowKeys(dataSource);

    return (
      <Container>
        <HeaderDiv>
          <LeftSide>
            <TitleStyled>Cancelled Operations</TitleStyled>
          </LeftSide>
        </HeaderDiv>
        <WorkOperationsTable
          parentStyledImportant
          columns={columns}
          dataSource={dataSource}
          expandedRowKeys={expandedRowKeys}
          onExpand={handleExpandRow}
        />
      </Container>
    );
  }

  return null;
}
